import type { SubscriptionPaymentMode } from "@/modules/billing/subscriptions/subscriptionScheduleUtils";

export type BillingTabId =
  | "proposals"
  | "invoices"
  | "subscriptions"
  | "reports"
  | "catalog";

export type { SubscriptionPaymentMode };

export type SubscriptionSubview = "overview" | "invoices" | "cards" | "edit";

export type SubscriptionDetailTab = Exclude<SubscriptionSubview, "edit">;

export const SUBSCRIPTION_DETAIL_TABS: Array<{
  value: SubscriptionDetailTab;
  label: string;
}> = [
  { value: "overview", label: "Overview" },
  { value: "invoices", label: "Invoice history" },
  { value: "cards", label: "Saved cards" },
];

export const BILLING_TABS: Array<{ id: BillingTabId; label: string }> = [
  { id: "proposals", label: "Proposals" },
  { id: "invoices", label: "Invoices" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "reports", label: "Reports" },
  { id: "catalog", label: "Products & services" },
];

export const buildBillingProposalsListPath = () =>
  "/billing?tab=proposals";

export const resolveBillingTab = (params: URLSearchParams): BillingTabId => {
  const tab = params.get("tab");
  if (tab === "proposals") return "proposals";
  if (tab === "subscriptions") return "subscriptions";
  if (tab === "reports") return "reports";
  if (tab === "catalog") return "catalog";
  return "invoices";
};

export const resolveSubscriptionSubview = (
  params: URLSearchParams,
): SubscriptionSubview => {
  const subview = params.get("subview");
  if (subview === "invoices" || subview === "edit" || subview === "cards") {
    return subview;
  }
  return "overview";
};

export const resolveSubscriptionPaymentMode = (
  params: URLSearchParams,
): SubscriptionPaymentMode | null => {
  const value = params.get("payment_mode");
  if (
    value === "saved_card" ||
    value === "staff_card" ||
    value === "request_setup"
  ) {
    return value;
  }
  return null;
};

export const buildSubscriptionEditSearchParams = (
  subscriptionId: string,
  options?: {
    paymentMode?: SubscriptionPaymentMode | null;
  },
) => {
  const next = new URLSearchParams();
  next.set("tab", "subscriptions");
  next.set("subscription", subscriptionId);
  next.set("subview", "edit");
  if (options?.paymentMode) {
    next.set("payment_mode", options.paymentMode);
  }
  return next;
};

export const buildSubscriptionDetailSearchParams = (
  subscriptionId: string,
  subview: SubscriptionSubview = "overview",
) => {
  const next = new URLSearchParams();
  next.set("tab", "subscriptions");
  next.set("subscription", subscriptionId);
  if (subview !== "overview") {
    next.set("subview", subview);
  }
  return next;
};

export const buildBillingInvoiceDetailPath = (invoiceId: string) =>
  `/billing?tab=invoices&invoice=${encodeURIComponent(String(invoiceId))}`;

/**
 * Params for a billing tab click. Clicking Invoices or Subscriptions always
 * lands on its list (an open invoice / subscription closes); Reports keeps
 * its filters.
 */
export const buildBillingTabSearchParams = (
  tab: BillingTabId,
  current: URLSearchParams,
) => {
  const next = new URLSearchParams();

  if (tab === "proposals") {
    next.set("tab", "proposals");
    return next;
  }

  if (tab === "subscriptions") {
    next.set("tab", "subscriptions");
    return next;
  }

  if (tab === "reports") {
    next.set("tab", "reports");
    const report = current.get("report");
    if (report) next.set("report", report);
    const period = current.get("period");
    if (period) next.set("period", period);
    const customer = current.get("customer");
    if (customer) next.set("customer", customer);
    const product = current.get("product");
    if (product) next.set("product", product);
    const churn = current.get("churn");
    if (churn) next.set("churn", churn);
    const newSubs = current.get("new_subs");
    if (newSubs) next.set("new_subs", newSubs);
    const newMrr = current.get("new_mrr");
    if (newMrr) next.set("new_mrr", newMrr);
    const compare = current.get("compare");
    if (compare) next.set("compare", compare);
    return next;
  }

  if (tab === "catalog") {
    next.set("tab", "catalog");
    return next;
  }

  return next;
};

export const isBillingSubscriptionWorkspace = (
  pathname: string,
  search: string,
) => {
  if (!pathname.startsWith("/billing")) return false;
  const params = new URLSearchParams(search);
  return (
    resolveBillingTab(params) === "subscriptions" &&
    Boolean(params.get("subscription"))
  );
};
